import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    linkedSignal,
    output,
    signal,
    ViewEncapsulation,
} from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { WorkflowHttpActionComponent } from "./workflow.http.action.component";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { DataIngestionService } from "../../data-ingestion/data.ingestion.service";
import { INGESTION_POINT_TYPE_ID } from "../../data-ingestion/data.ingestion.const";
import { WorkflowService } from "../service/workflow.service";
import { Workflow } from "../service/Workflow";
import { s25Resource } from "../../../util/s25.resource";
import { S25Util } from "../../../util/s25-util";
import { WorkflowFieldReferenceHelperComponent } from "../workflow.field.reference.helper.component";
import { Bind } from "../../../decorators/bind.decorator";
import { CreateEventActionComponent } from "./workflow.create.event.action.component";
import { WorkflowActionInputComponent } from "./workflow.action.input.component";
import { BudgetPlanService } from "../../student-engagement/budget/budget-plan/budget.plan.service";
import { BudgetLabelPhrases, BudgetLabels } from "../../student-engagement/budget/budget.labels";
import { RegisterActionComponent } from "./workflow.register.action.component";
import { SetOrgStatusActionComponent } from "./workflow.set.org.status.action.component";
import { SetContactRoleActionComponent } from "./workflow.set.contact.role.action.component";
import { AddContactRolesActionComponent } from "./workflow.add.contact.roles.action.component";
import { CreateOrgActionComponent } from "./workflow.create.org.action.component";
import { EditEventActionComponent } from "./workflow.edit.event.action.component";
import { EditOrgActionComponent } from "./workflow.edit.org.action.component";
import { AddCategoriesActionComponent } from "./workflow.add.categories.action.component";
import { AddCustomAttributesActionComponent } from "./workflow.add.custom.attributes.action.component";
import { AddOrgCategoriesActionComponent } from "./workflow.add.org.categories.action.component";
import { AddOrgCustomAttributesActionComponent } from "./workflow.add.org.custom.attributes.action.component";
import { AddCandidateActionComponent } from "./workflow.add.candidate.action.component";
import { AssignLive25DraftEventActionComponent } from "./workflow.assign.live25.draft.event.action.component";
import { SetSecurityGroupActionComponent } from "./workflow.set.security.group.action.component";
import { S25CheckboxModule } from "../../s25-checkbox/s25.checkbox.module";
import { WorkflowFormContext } from "./workflow.form.context";
import { WorkflowActionAutoMapService, clearStaleFieldReferences } from "../workflow.action.auto.map.service";
import { PreferenceService } from "../../../services/preference.service";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { getActionStableId } from "../workflow.action.grouping";

@Component({
    standalone: true,
    selector: "s25-ng-workflow-action",
    providers: [WorkflowFormContext],
    template: `
        @if (action().isSystemAction) {
            <!-- System Action Configuration -->
            <div class="form-group">
                <label>System Action Type</label>
                <select
                    [(ngModel)]="action().actionCode"
                    (ngModelChange)="onSystemActionTypeChange()"
                    class="form-control"
                >
                    <option value="">Select System Action</option>
                    @for (systemAction of systemActions; track systemAction.actionCode) {
                        <option [value]="systemAction.actionCode">
                            {{ systemAction.displayName }}
                        </option>
                    }
                </select>
                @if (selectedSystemActionDescription()) {
                    <p class="form-text">{{ selectedSystemActionDescription() }}</p>
                }
            </div>

            @if (!skipFormContext()) {
                <!-- Form Context Selection for this Action -->
                <div class="form-group">
                    <label>Form Context *</label>
                    <s25-ingestion-point-dropdown
                        [value]="formContext()"
                        (valueChange)="onFormContextDropdownChange($event)"
                        [enabledOnly]="true"
                    />
                    <small class="form-text">
                        This action needs form context to know which fields are available for payload building.
                    </small>
                </div>
            }

            @if (action().actionCode && !skipFieldHelper()) {
                <div class="form-group">
                    <label>Field Reference Helper</label>
                    <workflow-field-reference-helper [formFields]="formFields.value() || []" />
                    @if (autoMapOnFormContextChange() && formContext()?.itemId) {
                        <button
                            type="button"
                            class="aw-button aw-button--outline auto-map-fields-button"
                            (click)="onExplicitAutoMap()"
                        >
                            @if (workflowFormAssistanceEnabled.value()) {
                                <s25-ng-icon [type]="'aiSparkles'" [ariaLabel]="false" class="ai-sparkles-adjacent" />
                            }
                            Auto-map fields
                        </button>
                    }
                </div>
            }

            @if (action().actionCode === "SPAWN_WORKFLOW") {
                <!-- Spawn Form Configuration -->
                <div class="form-group">
                    <s25-ng-checkbox
                        [(value)]="action().spawnReuseSameItem"
                        (valueChange)="onSpawnReuseSameItemChange()"
                        >Reuse Same Form Submission</s25-ng-checkbox
                    >
                    <small class="enabled-help">
                        If checked, reuse the same form submission and compose stages into the current workflow process.
                        If unchecked, create a new form submission and spawn a separate child workflow instance.
                    </small>
                </div>

                @if (action().spawnReuseSameItem) {
                    <div class="form-group">
                        <label>Workflow to Spawn *</label>
                        <select
                            [(ngModel)]="action().spawnWorkflowProcessId"
                            (ngModelChange)="changed.emit()"
                            class="form-control"
                        >
                            <option [value]="undefined">-- Select Workflow Process --</option>
                            @for (workflow of availableWorkflows() || []; track workflow.workflowProcessId) {
                                <option [value]="workflow.workflowProcessId">
                                    {{ workflow.workflowProcessName }}
                                </option>
                            }
                        </select>
                        <small class="form-text">Select the workflow process to spawn when this action executes.</small>
                    </div>
                }

                <div class="form-group">
                    <label>Action Handling Strategy *</label>
                    <select
                        [(ngModel)]="action().spawnActionHandling"
                        (ngModelChange)="changed.emit()"
                        class="form-control"
                    >
                        <option [value]="undefined">-- Select Strategy --</option>
                        <option value="KEEP_ALL">Keep All Actions</option>
                        <option value="SKIP_ALL">Skip All Actions</option>
                        <option value="SKIP_TERMINAL">Skip Terminal Actions</option>
                        <option value="MOVE_TERMINAL_TO_PARENT">Move Terminal Actions to Parent</option>
                        <option value="MOVE_ALL_TO_PARENT">Move All Actions to Parent</option>
                    </select>
                    <small class="form-text">How to handle actions from the spawned child workflow.</small>
                </div>

                @if (!action().spawnReuseSameItem) {
                    <!-- Spawn Mode Toggle: Submitter (single) vs Iterator (per-entry) -->
                    <div class="form-group">
                        <label>Spawn Mode *</label>
                        <select
                            [ngModel]="isIteratorMode() ? 'iterator' : 'submitter'"
                            (ngModelChange)="onSpawnModeChange($event)"
                            class="form-control"
                        >
                            <option value="submitter">Spawn to Submitter</option>
                            <option value="iterator">Spawn to Contacts (Iterator)</option>
                        </select>
                        <small class="form-text">
                            "Submitter" spawns one child for the original submitter. "Iterator" spawns a child for each
                            entry in a JSON array field, using the dispatch mapping to select the target form per entry.
                        </small>
                    </div>

                    @if (isIteratorMode()) {
                        <!-- Iterator Configuration -->
                        <div class="iterator-config">
                            <div class="form-group">
                                <label>Array Field to Iterate *</label>
                                <select
                                    [(ngModel)]="action().spawnIteratorFieldDefinitionId"
                                    (ngModelChange)="onIteratorFieldChange()"
                                    class="form-control"
                                >
                                    <option [value]="undefined">-- Select Array Field --</option>
                                    @for (field of jsonArrayFields(); track field.fieldDefinitionId) {
                                        <option [ngValue]="field.fieldDefinitionId">
                                            {{ field.fieldLabel }} ({{ field.fieldType }})
                                        </option>
                                    }
                                </select>
                                <small class="form-text">
                                    Select the JSON array field whose entries will each spawn a child form.
                                </small>
                            </div>

                            @if (dispatchFieldTypes().length) {
                                <div class="form-group">
                                    <label>Dispatch Property *</label>
                                    <select
                                        [(ngModel)]="action().spawnDispatchFieldType"
                                        (ngModelChange)="changed.emit()"
                                        class="form-control"
                                    >
                                        <option [value]="undefined">-- Select Property --</option>
                                        @for (ft of dispatchFieldTypes(); track ft.value) {
                                            <option [value]="ft.value">{{ ft.label }}</option>
                                        }
                                    </select>
                                    <small class="form-text">
                                        Which entry property to use for dispatch lookup. Each entry's value for this
                                        property is matched against the dispatch table below.
                                    </small>
                                </div>
                            } @else if (action().spawnIteratorFieldDefinitionId) {
                                <small class="form-text">
                                    This field type has no dispatch property. Use a fallback mapping to select the
                                    target form for all entries.
                                </small>
                            }

                            <!-- Dispatch Mapping Table -->
                            <div class="form-group">
                                <label>Dispatch Mappings</label>
                                <div class="dispatch-table">
                                    <div class="dispatch-header">
                                        <span class="dispatch-col-value">Value</span>
                                        <span class="dispatch-col-form">Target Form</span>
                                        <span class="dispatch-col-action"></span>
                                    </div>
                                    @for (
                                        mapping of action().dispatchMappings || [];
                                        track mapping.dispatchValue;
                                        let i = $index
                                    ) {
                                        <div class="dispatch-row">
                                            @if (mapping.dispatchValue === "__fallback__") {
                                                <span class="dispatch-col-value dispatch-fallback-label">
                                                    Default / Any Other Value
                                                </span>
                                            } @else if (action().spawnDispatchFieldType === "ROLE") {
                                                <s25-ng-dropdown-search-criteria
                                                    class="dispatch-col-value"
                                                    [type]="'organizationRoles'"
                                                    [value]="getDispatchValueDropdown(mapping)"
                                                    (valueChange)="onDispatchValueChange(mapping, $event)"
                                                    [placeholder]="'Select Role'"
                                                />
                                            } @else {
                                                <input
                                                    [(ngModel)]="mapping.dispatchValue"
                                                    (ngModelChange)="changed.emit()"
                                                    class="form-control dispatch-col-value"
                                                    placeholder="e.g., 5"
                                                />
                                            }
                                            <s25-ingestion-point-dropdown
                                                class="dispatch-col-form"
                                                [value]="getDispatchFormDropdown(mapping)"
                                                (valueChange)="onDispatchFormChange(mapping, $event)"
                                                [enabledOnly]="true"
                                            />
                                            <button
                                                class="btn btn-sm btn-danger dispatch-col-action"
                                                (click)="removeDispatchMapping(i)"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    }
                                    <div class="dispatch-actions">
                                        @if (dispatchFieldTypes().length) {
                                            <button
                                                class="btn btn-sm btn-outline-primary"
                                                (click)="addDispatchMapping()"
                                            >
                                                + Add Mapping
                                            </button>
                                        }
                                        @if (!hasFallbackMapping()) {
                                            <button
                                                class="btn btn-sm btn-outline-secondary"
                                                (click)="addFallbackMapping()"
                                            >
                                                + Add Fallback
                                            </button>
                                        }
                                    </div>
                                </div>
                                <small class="form-text">
                                    Map dispatch values to target forms. Entries that don't match any value are skipped
                                    unless a fallback is configured.
                                </small>
                            </div>
                        </div>
                    } @else {
                        <!-- Submitter mode: existing Item Type and Form selection -->
                        <div class="form-group">
                            <label>Item Type *</label>
                            <select
                                [ngModel]="action().spawnChildParentItemTypeId"
                                (ngModelChange)="onChildParentItemTypeChange($event)"
                                class="form-control"
                            >
                                <option [value]="undefined">-- Select Type --</option>
                                <option [ngValue]="INGESTION_POINT_TYPE_ID">Ingestion Point (Form)</option>
                                <option [ngValue]="BUDGET_PLAN_OBJECT_TYPE">{{ budgetLabels.BUDGET }}</option>
                            </select>
                            <small class="form-text">
                                Select the type of item to use for the child workflow submission.
                            </small>
                        </div>

                        @if (isIngestionPointType()) {
                            <div class="form-group">
                                <label>Select Form *</label>
                                <s25-ingestion-point-dropdown
                                    [value]="childFormContext()"
                                    (valueChange)="onChildFormContextChange($event)"
                                    [enabledOnly]="true"
                                />
                                <small class="form-text">
                                    Select the form to use for the new child workflow submission. Required when creating
                                    a new form submission.
                                </small>
                            </div>
                        }

                        @if (isBudgetPlanType()) {
                            <div class="form-group">
                                <label>{{ budgetLabelPhrases.selectOrganizationForBudget }}</label>
                                <s25-ng-dropdown-search-criteria
                                    type="organizations"
                                    [value]="selectedOrganization()"
                                    (valueChange)="onOrganizationChange($event)"
                                    class="form-control"
                                />
                                <small class="form-text"> {{ budgetLabelPhrases.loadBudgetsFromOrgHint }} </small>
                            </div>

                            @if (selectedOrganization()?.itemId) {
                                <div class="form-group">
                                    <label>{{ budgetLabelPhrases.selectBudgetRequired }}</label>
                                    <select
                                        [(ngModel)]="action().spawnChildParentItemId"
                                        (ngModelChange)="changed.emit()"
                                        class="form-control"
                                    >
                                        <option [value]="undefined">
                                            {{ budgetLabelPhrases.selectBudgetPlaceholder }}
                                        </option>
                                        @for (plan of availableBudgetPlans.value() || []; track plan.budgetPlanId) {
                                            <option [value]="plan.budgetId">
                                                {{ plan.name }}
                                            </option>
                                        }
                                    </select>
                                    <small class="form-text">
                                        {{ budgetLabelPhrases.spawnChildBudgetHint }}
                                        when creating a new form submission.
                                    </small>
                                </div>
                            }
                        }
                    }
                }
            } @else {
                <div class="form-group">
                    <label>System Action Inputs</label>
                    <div class="system-inputs">
                        @if (action().actionCode === "CREATE_EVENT") {
                            <s25-ng-workflow-create-event-action
                                [(inputs)]="action().inputs"
                                (inputsChange)="changed.emit()"
                                [inputIdGenerator]="inputIdGenerator()"
                                [parentStage]="parentStage()"
                            />
                        } @else if (action().actionCode === "REGISTER") {
                            <s25-ng-workflow-register-action
                                [(inputs)]="action().inputs"
                                (inputsChange)="changed.emit()"
                                [inputIdGenerator]="inputIdGenerator()"
                            />
                        } @else if (action().actionCode === "SET_ORG_STATUS") {
                            <s25-ng-workflow-set-org-status-action
                                [(inputs)]="action().inputs"
                                (inputsChange)="changed.emit()"
                                [inputIdGenerator]="inputIdGenerator()"
                            />
                        } @else if (action().actionCode === "SET_CONTACT_ROLE") {
                            <s25-ng-workflow-set-contact-role-action
                                [(inputs)]="action().inputs"
                                (inputsChange)="changed.emit()"
                                [inputIdGenerator]="inputIdGenerator()"
                            />
                        } @else if (action().actionCode === "ADD_CONTACT_ROLES") {
                            <s25-ng-workflow-add-contact-roles-action
                                [(inputs)]="action().inputs"
                                (inputsChange)="changed.emit()"
                                [inputIdGenerator]="inputIdGenerator()"
                            />
                        } @else if (action().actionCode === "CREATE_ORG") {
                            <s25-ng-workflow-create-org-action
                                [(inputs)]="action().inputs"
                                (inputsChange)="changed.emit()"
                                [inputIdGenerator]="inputIdGenerator()"
                            />
                        } @else if (action().actionCode === "EDIT_EVENT") {
                            <s25-ng-workflow-edit-event-action
                                [(inputs)]="action().inputs"
                                (inputsChange)="changed.emit()"
                                [inputIdGenerator]="inputIdGenerator()"
                                [parentStage]="parentStage()"
                            />
                        } @else if (action().actionCode === "EDIT_ORG") {
                            <s25-ng-workflow-edit-org-action
                                [(inputs)]="action().inputs"
                                (inputsChange)="changed.emit()"
                                [inputIdGenerator]="inputIdGenerator()"
                            />
                        } @else if (action().actionCode === "ADD_CATEGORIES") {
                            <s25-ng-workflow-add-categories-action
                                [(inputs)]="action().inputs"
                                (inputsChange)="changed.emit()"
                                [inputIdGenerator]="inputIdGenerator()"
                            />
                        } @else if (action().actionCode === "ADD_CUSTOM_ATTRIBUTES") {
                            <s25-ng-workflow-add-custom-attributes-action
                                [(inputs)]="action().inputs"
                                (inputsChange)="changed.emit()"
                                [inputIdGenerator]="inputIdGenerator()"
                            />
                        } @else if (action().actionCode === "ADD_ORG_CATEGORIES") {
                            <s25-ng-workflow-add-org-categories-action
                                [(inputs)]="action().inputs"
                                (inputsChange)="changed.emit()"
                                [inputIdGenerator]="inputIdGenerator()"
                            />
                        } @else if (action().actionCode === "ADD_ORG_CUSTOM_ATTRIBUTES") {
                            <s25-ng-workflow-add-org-custom-attributes-action
                                [(inputs)]="action().inputs"
                                (inputsChange)="changed.emit()"
                                [inputIdGenerator]="inputIdGenerator()"
                            />
                        } @else if (action().actionCode === "ADD_CANDIDATE") {
                            <s25-ng-workflow-add-candidate-action
                                [(inputs)]="action().inputs"
                                (inputsChange)="changed.emit()"
                                [inputIdGenerator]="inputIdGenerator()"
                            />
                        } @else if (action().actionCode === "ASSIGN_LIVE_25_DRAFT_EVENT") {
                            <s25-ng-workflow-assign-live25-draft-event-action
                                [(inputs)]="action().inputs"
                                (inputsChange)="changed.emit()"
                                [inputIdGenerator]="inputIdGenerator()"
                                [parentStage]="parentStage()"
                            />
                        } @else if (action().actionCode === "SET_SECURITY_GROUP") {
                            <s25-ng-workflow-set-security-group-action [action]="action()" />
                        } @else {
                            @for (inputDef of systemActionInputs(); track inputDef.inputCode) {
                                <s25-ng-workflow-action-input
                                    class="system-input"
                                    [value]="getActionInputValue(inputDef.inputCode)"
                                    (valueChange)="setActionInputValue(inputDef.inputCode, $event)"
                                    [label]="inputDef.label"
                                    [type]="inputDef.type"
                                    [required]="inputDef.required"
                                    [help]="inputDef.help"
                                    [acceptedAnswerTypes]="inputDef.acceptedAnswerTypes"
                                    [acceptedFieldTypes]="inputDef.acceptedFieldTypes"
                                />
                            }
                        }
                    </div>
                </div>
            }
        } @else {
            @if (!skipFormContext()) {
                <!-- Form Context Selection for this Action -->
                <div class="form-group">
                    <label>Form Context *</label>
                    <s25-ingestion-point-dropdown
                        [value]="formContext()"
                        (valueChange)="onFormContextDropdownChange($event)"
                        [enabledOnly]="true"
                    />
                    <small class="form-text">
                        This action needs form context to know which fields are available for payload building.
                    </small>
                </div>
            }

            <div class="form-group">
                <label>Field Reference Helper</label>
                <workflow-field-reference-helper [formFields]="formFields.value() || []" />
            </div>

            <s25-ng-workflow-http-action
                [action]="action()"
                [formFields]="formFields.value() || []"
                (changed)="changed.emit()"
            />
        }

        <div class="form-group">
            <label>Description</label>
            <textarea
                [(ngModel)]="action().description"
                class="form-control"
                rows="2"
                placeholder="Describe what this action does (optional)"
            ></textarea>
        </div>

        <div class="form-group">
            <s25-ng-checkbox [(value)]="action().defaultEnabled" (valueChange)="changed.emit()"
                >Default Enabled</s25-ng-checkbox
            >
            <small class="enabled-help"
                >Unchecked actions are disabled by default. Enable/disable rules are configured in Action Rules
                below.</small
            >
        </div>
    `,
    styles: `
        :host {
            padding: 16px;
            display: block;
        }

        .form-group {
            margin-bottom: 16px;
        }

        .form-group label {
            display: block;
            margin-bottom: 4px;
            font-weight: 500;
            color: #555;
        }

        .form-control {
            width: 100%;
            padding: 8px 12px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 14px;
            box-sizing: border-box;
        }

        .form-control:focus {
            outline: none;
            border-color: #007bff;
            box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
        }

        select.form-control {
            background-color: white;
            background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
            background-position: right 8px center;
            background-repeat: no-repeat;
            background-size: 16px 16px;
            padding-right: 40px;
        }

        .form-text {
            color: #666;
            font-size: 12px;
            margin-top: 4px;
            display: block;
        }

        .system-inputs {
            border: 1px solid #ddd;
            border-radius: 6px;
            padding: 12px;
            background: #f9f9f9;
        }

        .system-input {
            margin-bottom: 16px;
        }

        .system-input:last-child {
            margin-bottom: 0;
        }

        .enabled-help {
            color: #666;
            display: block;
        }

        .iterator-config {
            border: 1px solid #d0e0f0;
            border-radius: 6px;
            padding: 12px;
            background: #f0f6ff;
            margin-bottom: 16px;
        }

        .dispatch-table {
            border: 1px solid #ddd;
            border-radius: 4px;
            overflow: visible;
        }

        .dispatch-header {
            display: flex;
            gap: 8px;
            padding: 8px 12px;
            background: #eee;
            font-weight: 500;
            font-size: 13px;
        }

        .dispatch-row {
            display: flex;
            gap: 8px;
            padding: 8px 12px;
            align-items: center;
            border-top: 1px solid #eee;
        }

        .dispatch-col-value {
            flex: 1;
            min-width: 100px;
        }

        .dispatch-col-value .ui-select-dropdown {
            position: relative;
        }

        .dispatch-col-form {
            flex: 2;
        }

        .dispatch-col-action {
            flex: 0 0 auto;
        }

        .dispatch-fallback-label {
            font-style: italic;
            color: #666;
            display: flex;
            align-items: center;
        }

        .dispatch-actions {
            padding: 8px 12px;
            border-top: 1px solid #eee;
            display: flex;
            gap: 8px;
        }

        .auto-map-fields-button {
            margin-top: 8px;
            display: inline-flex;
            align-items: center;
            gap: 0.35em;
        }

        .ai-sparkles-adjacent {
            flex-shrink: 0;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    imports: [
        ReactiveFormsModule,
        S25DropdownPaginatedModule,
        WorkflowHttpActionComponent,
        FormsModule,
        WorkflowFieldReferenceHelperComponent,
        CreateEventActionComponent,
        WorkflowActionInputComponent,
        RegisterActionComponent,
        SetOrgStatusActionComponent,
        SetContactRoleActionComponent,
        AddContactRolesActionComponent,
        CreateOrgActionComponent,
        EditOrgActionComponent,
        EditEventActionComponent,
        AddCategoriesActionComponent,
        AddCustomAttributesActionComponent,
        AddOrgCategoriesActionComponent,
        AddOrgCustomAttributesActionComponent,
        AddCandidateActionComponent,
        AssignLive25DraftEventActionComponent,
        SetSecurityGroupActionComponent,
        S25CheckboxModule,
        S25IconComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowActionComponent {
    readonly budgetLabels = BudgetLabels;
    readonly budgetLabelPhrases = BudgetLabelPhrases;

    readonly action = input.required<Workflow.WorkflowAction>();
    readonly inputIdGenerator = input.required<() => number>();
    readonly skipFormContext = input<boolean>(false);
    readonly skipFieldHelper = input<boolean>(false);
    readonly autoMapOnFormContextChange = input<boolean>(false);
    readonly workflowProcess = input<Workflow.WorkflowProcess | undefined>();
    readonly parentStage = input<Workflow.WorkflowStage | undefined>();
    readonly changed = output<void>();
    readonly formContextNavigated = output<{ actionId: number; formId: number | null }>();

    readonly workflowFormAssistanceEnabled = s25Resource({
        loader: async () => {
            try {
                const prefs = await PreferenceService.getPreferences(["config_ai_form_workflow_builder"], "S");
                return prefs?.config_ai_form_workflow_builder?.value === "T";
            } catch {
                return false;
            }
        },
    });

    private readonly formCtx = inject(WorkflowFormContext);

    readonly systemActions = Object.values(WorkflowService.SYSTEM_ACTIONS);
    readonly selectedSystemActionDescription = computed(() => {
        const actionCode = this.action().actionCode;
        if (!actionCode) return undefined;
        const systemAction = WorkflowService.SYSTEM_ACTIONS[actionCode as keyof typeof WorkflowService.SYSTEM_ACTIONS];
        const description = systemAction?.description?.trim();
        return description || undefined;
    });
    readonly systemActionInputs = computed(() => {
        const actionCode = this.action().actionCode;
        const systemAction = WorkflowService.SYSTEM_ACTIONS[actionCode as keyof typeof WorkflowService.SYSTEM_ACTIONS];
        if (systemAction && "inputs" in systemAction && systemAction.inputs) return systemAction.inputs;
        return [];
    });
    readonly defaultFormContext = s25Resource({
        params: () => this.action(),
        loader: async ({ params: action, abortSignal }) => {
            if (!action.actionItemId || Number(action.actionItemTypeId) !== INGESTION_POINT_TYPE_ID) return;
            const [ingestionPoint, error] = await S25Util.Maybe(
                DataIngestionService.getIngestionPoint(action.actionItemId, { abortSignal }),
            );
            if (error) {
                console.error("Error initializing form context for action:", error);
                return;
            }
            if (!ingestionPoint) return;
            const formContext: DropDownItem = {
                txt: ingestionPoint.ingestionPointName || `Form ${ingestionPoint.ingestionPointId}`,
                val: ingestionPoint.ingestionPointId,
                itemTypeId: INGESTION_POINT_TYPE_ID,
                itemId: ingestionPoint.ingestionPointId,
                itemName: ingestionPoint.ingestionPointName,
                data: ingestionPoint,
            };
            return formContext;
        },
    });
    readonly formContext = linkedSignal(() => this.defaultFormContext.value());
    readonly formFields = s25Resource({
        params: () => this.formContext(),
        loader: async ({ params: formContext }) => {
            if (!formContext?.itemId) return [];
            const [fields, error] = await S25Util.Maybe(
                DataIngestionService.getFieldDefinitions(Number(formContext.itemId)),
            );
            if (error) {
                console.error("Error loading field definitions for action:", error);
            }
            return fields || [];
        },
    });
    readonly availableWorkflows = signal<Workflow.WorkflowProcess[]>([]);

    /** Last form id processed by {@link onFormContextChange} (per action instance). */
    private lastSyncedFormContextItemId: number | null | undefined;

    // Iterator mode: true when the user has selected iterator mode (field may not be picked yet)
    readonly isIteratorMode = linkedSignal(() => this.action().spawnIteratorFieldDefinitionId != null);

    // JSON array fields from the form context, available for the iterator field picker
    readonly jsonArrayFields = computed(() => {
        const fields = this.formFields.value() || [];
        return fields.filter(
            (f) => !f.isDeleted && !!f.fieldType && ["ORG_CONTACT_ROLE", "CONTACT"].includes(f.fieldType),
        );
    });

    // Which dispatch properties are available depends on the selected array field's type
    static readonly DISPATCH_FIELD_TYPES_BY_FIELD: Record<string, { value: string; label: string }[]> = {
        ORG_CONTACT_ROLE: [{ value: "ROLE", label: "Role" }],
        CONTACT: [],
    };

    readonly dispatchFieldTypes = linkedSignal(() => {
        const selectedFieldId = this.action().spawnIteratorFieldDefinitionId;
        return this.resolveDispatchFieldTypes(selectedFieldId);
    });

    private resolveDispatchFieldTypes(selectedFieldId: number | undefined): { value: string; label: string }[] {
        if (!selectedFieldId) return [];
        const selectedField = this.jsonArrayFields().find((f) => f.fieldDefinitionId === selectedFieldId);
        if (!selectedField?.fieldType) return [];
        return WorkflowActionComponent.DISPATCH_FIELD_TYPES_BY_FIELD[selectedField.fieldType] ?? [];
    }

    readonly INGESTION_POINT_TYPE_ID = INGESTION_POINT_TYPE_ID;
    readonly BUDGET_PLAN_OBJECT_TYPE = Workflow.BUDGET_PLAN_OBJECT_TYPE;

    readonly spawnChildParentItemTypeIdSignal = signal<number | undefined>(undefined);

    readonly isIngestionPointType = computed(() => {
        const typeId = this.spawnChildParentItemTypeIdSignal() ?? this.action().spawnChildParentItemTypeId;
        return typeId === INGESTION_POINT_TYPE_ID;
    });

    readonly isBudgetPlanType = computed(() => {
        const typeId = this.spawnChildParentItemTypeIdSignal() ?? this.action().spawnChildParentItemTypeId;
        return typeId === Workflow.BUDGET_PLAN_OBJECT_TYPE;
    });

    readonly childFormContext = linkedSignal(() => {
        const action = this.action();
        if (!action.spawnChildParentItemId || action.spawnChildParentItemTypeId !== INGESTION_POINT_TYPE_ID)
            return null;
        // Return a simple object that matches DropDownItem structure
        // The actual ingestion point will be loaded by the dropdown component
        return {
            itemId: action.spawnChildParentItemId,
            itemTypeId: INGESTION_POINT_TYPE_ID,
        } as DropDownItem | null;
    });

    readonly selectedOrganization = signal<DropDownItem | null>(null);

    readonly availableBudgetPlans = s25Resource({
        params: () => {
            const org = this.selectedOrganization();
            return org?.itemId ? Number(org.itemId) : null;
        },
        loader: async ({ params: organizationId, abortSignal }) => {
            if (!organizationId) return [];
            try {
                const plans = await BudgetPlanService.getBudgetPlans(organizationId);
                return plans || [];
            } catch (error) {
                console.error("Error loading budget plans:", error);
                return [];
            }
        },
    });

    constructor() {
        // Sync spawnChildParentItemTypeIdSignal with action
        effect(() => {
            const typeId = this.action().spawnChildParentItemTypeId;
            this.spawnChildParentItemTypeIdSignal.set(typeId);
        });

        // Sync form fields into DI context for WorkflowActionInputComponent descendants
        effect(() => {
            this.formCtx.formFields.set(this.formFields.value() ?? []);
        });

        // Load workflows if action is set to SPAWN_WORKFLOW
        effect(() => {
            const action = this.action();
            if (action.isSystemAction && action.actionCode === "SPAWN_WORKFLOW") {
                this.onSystemActionTypeChange();
            }
        });
        effect(() => {
            getActionStableId(this.action());
            this.lastSyncedFormContextItemId = undefined;
        });
        effect(this.onFormContextChange);
    }

    @Bind
    async onFormContextDropdownChange(formContext: DropDownItem | null) {
        const nextFormId = formContext?.itemId != null ? Number(formContext.itemId) : null;
        const currentFormId = this.formContext()?.itemId != null ? Number(this.formContext()!.itemId) : null;
        if (nextFormId === currentFormId) return;

        this.formContext.set(formContext ?? undefined);
        this.syncActionFormContext(formContext);

        this.formContextNavigated.emit({
            actionId: getActionStableId(this.action()),
            formId: nextFormId,
        });

        const workflowProcess = this.workflowProcess();
        if (this.autoMapOnFormContextChange() && workflowProcess && nextFormId) {
            await this.formFields.reloadAndAwait();
            const targetFields = this.formFields.value() ?? [];
            if (targetFields.length) {
                await WorkflowActionAutoMapService.autoMapActionInputs(
                    this.action(),
                    targetFields,
                    workflowProcess,
                    undefined,
                    { mode: "automatic" },
                );
                this.changed.emit();
            }
        }
    }

    private syncActionFormContext(formContext: DropDownItem | null | undefined) {
        if (formContext?.itemId) {
            this.action().actionItemId = Number(formContext.itemId);
            this.action().actionItemTypeId = formContext.itemTypeId ?? INGESTION_POINT_TYPE_ID;
            return;
        }
        if (formContext === null) {
            this.action().actionItemId = undefined;
            this.action().actionItemTypeId = undefined;
        }
    }

    @Bind
    async onFormContextChange() {
        const formContext = this.formContext();
        const formId = formContext?.itemId != null ? Number(formContext.itemId) : null;

        if (
            formContext === undefined &&
            this.action().actionItemId != null &&
            Number(this.action().actionItemTypeId) === INGESTION_POINT_TYPE_ID
        ) {
            return;
        }

        const formContextChanged = formId !== this.lastSyncedFormContextItemId;
        this.lastSyncedFormContextItemId = formId;

        this.syncActionFormContext(formContext);

        const targetFields = this.formFields.value() ?? [];
        if (formId && targetFields.length) {
            const staleCleared = clearStaleFieldReferences(this.action(), targetFields);
            if (staleCleared) {
                this.changed.emit();
            }
        } else if (formContextChanged) {
            this.changed.emit();
        }
    }

    @Bind
    async onExplicitAutoMap(): Promise<void> {
        const workflowProcess = this.workflowProcess();
        const formId = this.formContext()?.itemId;
        if (!workflowProcess || !formId) {
            return;
        }
        await this.formFields.reloadAndAwait();
        const targetFields = this.formFields.value() ?? [];
        if (!targetFields.length) {
            return;
        }
        await WorkflowActionAutoMapService.autoMapActionInputs(
            this.action(),
            targetFields,
            workflowProcess,
            undefined,
            { mode: "explicit" },
        );
        this.changed.emit();
    }

    async onSystemActionTypeChange() {
        if (!this.action().actionCode) return;
        this.action().inputs = []; // Initialize inputs
        this.action().description = "";
        this.action().httpUrl = undefined;
        this.action().httpMethod = undefined;
        this.action().httpHeaders = undefined;
        this.action().jsonPayloadTemplate = undefined;

        // Load workflows if this is a SPAWN_WORKFLOW action
        if (this.action().actionCode === "SPAWN_WORKFLOW") {
            // Set default action handling strategy to KEEP_ALL if not already set
            if (!this.action().spawnActionHandling) {
                this.action().spawnActionHandling = "KEEP_ALL";
            }
            try {
                const workflows = await WorkflowService.getWorkflowProcesses(true);
                this.availableWorkflows.set(workflows || []);
            } catch (error) {
                console.error("Error loading workflows:", error);
                this.availableWorkflows.set([]);
            }
        } else {
            this.availableWorkflows.set([]);
        }

        this.changed.emit();
    }

    getActionInputValue(inputCode: string): string {
        const inputs = this.action().inputs;
        if (!inputs) return "";
        const input = inputs.find((i) => i.inputCode === inputCode);
        return input ? input.inputValue || "" : "";
    }

    setActionInputValue(inputCode: string, value: string) {
        const inputs = this.action().inputs;
        if (!inputs) {
            this.action().inputs = [];
        }

        const actionInputs = this.action().inputs!;
        let input = actionInputs.find((i) => i.inputCode === inputCode);
        if (!input) {
            input = {
                id: this.inputIdGenerator()(),
                inputCode: inputCode,
                inputValue: value,
            };
            actionInputs.push(input);
        } else {
            input.inputValue = value;
        }

        this.changed.emit();
    }

    onChildFormContextChange(formContext: DropDownItem | null) {
        const action = this.action();
        if (formContext?.itemId) {
            action.spawnChildParentItemId = Number(formContext.itemId);
            action.spawnChildParentItemTypeId = formContext.itemTypeId;
        } else {
            action.spawnChildParentItemId = undefined;
            action.spawnChildParentItemTypeId = undefined;
        }
        this.changed.emit();
    }

    onOrganizationChange(organization: DropDownItem | null) {
        this.selectedOrganization.set(organization);
        // Clear budget plan selection when organization changes
        if (this.action().spawnChildParentItemTypeId === Workflow.BUDGET_PLAN_OBJECT_TYPE) {
            this.action().spawnChildParentItemId = undefined;
        }
        this.changed.emit();
    }

    onChildParentItemTypeChange(value: number | undefined) {
        const action = this.action();
        // Set the new type
        action.spawnChildParentItemTypeId = value;
        // Update the signal to trigger computed updates
        this.spawnChildParentItemTypeIdSignal.set(value);
        // Clear the parent item ID and organization when type changes
        action.spawnChildParentItemId = undefined;
        this.selectedOrganization.set(null);
        this.changed.emit();
    }

    onSpawnReuseSameItemChange() {
        const action = this.action();
        if (action.spawnReuseSameItem) {
            // When reusing the same workflow item, clear child form context fields since they're not needed
            action.spawnChildParentItemId = undefined;
            action.spawnChildParentItemTypeId = undefined;
            // Also clear iterator fields when switching to reuse mode
            action.spawnIteratorFieldDefinitionId = undefined;
            action.spawnDispatchFieldType = undefined;
            action.dispatchMappings = undefined;
        } else {
            // When not reusing the same workflow item, clear spawnWorkflowProcessId since it's not needed
            action.spawnWorkflowProcessId = undefined;
        }
        this.changed.emit();
    }

    onIteratorFieldChange() {
        const action = this.action();
        const validTypes = this.resolveDispatchFieldTypes(action.spawnIteratorFieldDefinitionId);
        this.dispatchFieldTypes.set(validTypes);

        if (validTypes.length === 1) {
            action.spawnDispatchFieldType = validTypes[0].value;
        } else if (!validTypes.some((ft) => ft.value === action.spawnDispatchFieldType)) {
            action.spawnDispatchFieldType = undefined;
        }

        if (!validTypes.length && action.spawnIteratorFieldDefinitionId && !this.hasFallbackMapping()) {
            this.addFallbackMapping();
        }

        this.changed.emit();
    }

    onSpawnModeChange(mode: string) {
        const action = this.action();
        if (mode === "iterator") {
            this.isIteratorMode.set(true);
            action.spawnChildParentItemId = undefined;
            action.spawnChildParentItemTypeId = undefined;
            action.spawnIteratorFieldDefinitionId = undefined;
            action.spawnDispatchFieldType = undefined;
            action.dispatchMappings = [];
        } else {
            this.isIteratorMode.set(false);
            action.spawnIteratorFieldDefinitionId = undefined;
            action.spawnDispatchFieldType = undefined;
            action.dispatchMappings = undefined;
        }
        this.changed.emit();
    }

    getDispatchValueDropdown(mapping: Workflow.WorkflowActionDispatch): DropDownItem | null {
        if (!mapping.dispatchValue) return null;
        return { itemId: Number(mapping.dispatchValue) } as DropDownItem;
    }

    onDispatchValueChange(mapping: Workflow.WorkflowActionDispatch, selected: DropDownItem | null) {
        mapping.dispatchValue = selected?.itemId ? String(selected.itemId) : "";
        this.changed.emit();
    }

    getDispatchFormDropdown(mapping: Workflow.WorkflowActionDispatch): DropDownItem | null {
        if (!mapping.targetIngestionPointId) return null;
        return {
            itemId: mapping.targetIngestionPointId,
            itemTypeId: INGESTION_POINT_TYPE_ID,
        } as DropDownItem;
    }

    onDispatchFormChange(mapping: Workflow.WorkflowActionDispatch, formContext: DropDownItem | null) {
        mapping.targetIngestionPointId = formContext?.itemId ? Number(formContext.itemId) : (undefined as any);
        this.changed.emit();
    }

    addDispatchMapping() {
        const action = this.action();
        if (!action.dispatchMappings) action.dispatchMappings = [];
        action.dispatchMappings.push({
            workflowActionId: action.workflowActionId,
            dispatchValue: "",
            targetIngestionPointId: undefined,
        });
        this.changed.emit();
    }

    addFallbackMapping() {
        const action = this.action();
        if (!action.dispatchMappings) action.dispatchMappings = [];
        action.dispatchMappings.push({
            workflowActionId: action.workflowActionId,
            dispatchValue: "__fallback__",
            targetIngestionPointId: undefined,
        });
        this.changed.emit();
    }

    hasFallbackMapping(): boolean {
        return !!this.action().dispatchMappings?.some((m) => m.dispatchValue === "__fallback__");
    }

    removeDispatchMapping(index: number) {
        const mappings = this.action().dispatchMappings;
        if (mappings) {
            mappings.splice(index, 1);
            this.changed.emit();
        }
    }
}
